import { useDepsChanged } from './chunk-I37XHMWY.js';
import { CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './chunk-5WNNAWE3.js';
import { useTheme } from './chunk-77MV73QS.js';
import { Toaster as Toaster$1 } from 'sonner';
export { toast } from 'sonner';
import { jsx, jsxs } from 'react/jsx-runtime';
import * as React3 from 'react';
import { ShortcutManager } from '@authe/fe-commons/shortcuts';

function Toaster(props) {
  let resolvedMode = "light";
  try {
    resolvedMode = useTheme().resolvedMode;
  } catch {
  }
  return /* @__PURE__ */ jsx(
    Toaster$1,
    {
      theme: resolvedMode,
      className: "toaster group",
      toastOptions: {
        classNames: {
          toast: "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg",
          description: "group-[.toast]:text-muted-foreground",
          actionButton: "group-[.toast]:bg-brand group-[.toast]:text-brand-foreground",
          cancelButton: "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground",
          error: "group-[.toaster]:border-destructive/40"
        }
      },
      ...props
    }
  );
}
function CommandMenu({
  groups,
  open: controlledOpen,
  onOpenChange,
  placeholder = "Type a command or search\u2026",
  emptyMessage = "No results found.",
  toggleCombo = "mod+k"
}) {
  const [internalOpen, setInternalOpen] = React3.useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = React3.useCallback(
    (next) => {
      if (controlledOpen == null) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlledOpen, onOpenChange]
  );
  React3.useEffect(() => {
    const manager = new ShortcutManager();
    manager.register({
      combo: toggleCombo,
      description: "Open command menu",
      allowInInput: true,
      handler: () => setOpen(!open)
    });
    return manager.attach();
  }, [open, setOpen, toggleCombo]);
  return /* @__PURE__ */ jsxs(CommandDialog, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(CommandInput, { placeholder }),
    /* @__PURE__ */ jsxs(CommandList, { children: [
      /* @__PURE__ */ jsx(CommandEmpty, { children: emptyMessage }),
      groups.map((group, i) => /* @__PURE__ */ jsx(CommandGroup, { heading: group.heading, children: group.actions.map((action) => /* @__PURE__ */ jsxs(
        CommandItem,
        {
          value: [action.label, ...action.keywords ?? []].join(" "),
          disabled: action.disabled,
          onSelect: () => {
            setOpen(false);
            action.onSelect();
          },
          children: [
            action.icon,
            /* @__PURE__ */ jsx("span", { children: action.label }),
            action.shortcut && /* @__PURE__ */ jsx("span", { className: "ml-auto text-xs tracking-widest text-muted-foreground", children: action.shortcut })
          ]
        },
        action.id
      )) }, group.heading ?? `group-${i}`))
    ] })
  ] });
}
var listeners = /* @__PURE__ */ new Set();
function notifyDisplayPrefs() {
  for (const listener of [...listeners]) listener();
}
function useDisplayPref(read, write) {
  const [value, setValue] = React3.useState(read);
  const readRef = React3.useRef(read);
  readRef.current = read;
  const writeRef = React3.useRef(write);
  writeRef.current = write;
  React3.useEffect(() => {
    const listener = () => setValue(readRef.current());
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  const set = React3.useCallback((next) => {
    setValue(next);
    if (writeRef.current(next)) notifyDisplayPrefs();
  }, []);
  return [value, set];
}
function applyCap(next, cap) {
  if (!cap || !Array.isArray(next)) return next;
  if (cap.limit <= 0) return [];
  if (next.length <= cap.limit) return next;
  return cap.keep === "last" ? next.slice(-cap.limit) : next.slice(0, cap.limit);
}
function usePersistedCollection(options) {
  const { read, resetKey, subscribe } = options;
  const [value, setValue] = React3.useState(read);
  const valueRef = React3.useRef(value);
  const unpersisted = React3.useRef(false);
  const latest = React3.useRef(options);
  latest.current = options;
  if (useDepsChanged([resetKey])) {
    const loaded = read();
    valueRef.current = loaded;
    unpersisted.current = false;
    setValue(loaded);
  }
  React3.useEffect(() => subscribe?.(() => {
    if (unpersisted.current) return;
    const loaded = latest.current.read();
    valueRef.current = loaded;
    setValue(loaded);
  }), [subscribe, resetKey]);
  const commit = React3.useCallback((compute) => {
    const previous = latest.current.readBeforeCommit && !unpersisted.current ? latest.current.read() : valueRef.current;
    const next = applyCap(compute(previous), latest.current.cap);
    if (next === previous) return;
    valueRef.current = next;
    unpersisted.current = latest.current.write(next) === false;
    setValue(next);
  }, []);
  const clear = React3.useCallback(() => {
    const { empty, remove, write } = latest.current;
    valueRef.current = empty;
    if (remove) {
      remove();
      unpersisted.current = false;
    } else unpersisted.current = write(empty) === false;
    setValue(empty);
  }, []);
  return [value, commit, clear];
}

export { CommandMenu, Toaster, useDisplayPref, usePersistedCollection };
//# sourceMappingURL=chunk-DR4R3D5H.js.map
//# sourceMappingURL=chunk-DR4R3D5H.js.map